import dayjs, {Dayjs} from "dayjs";
import React, {useContext, useMemo, useRef, useState} from "react";
import styles from "./MSSlotCalendar.module.css";
import descriptionStyles from '../../styles/DescriptionStyle.module.css';
import {
    ClockCircleOutlined,
    WarningOutlined,
    CalendarOutlined,
    DollarOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    DatePicker,
    Descriptions,
    Divider, InputNumber,
    InputRef,
    Modal,
    notification,
    Popover,
    Select,
    Skeleton,
    Tooltip
} from "antd";
import Utils from "../../util/util";
import useTruck from "../../swr/useTruck";
import QuoteTruckImage from "../../components/Quote/QuoteTruckImage";
import QuoteContextProvider from "../../contexts/QuoteContext";
import useQuote from "../../swr/useQuote";
import {Link} from "react-router-dom";
import {DescriptionsItemType} from "antd/es/descriptions";
import TruckCommentList from "../../components/Quote/TruckCommentList";
import {
    FamilyLineDetail,
    getMotorFamilyLineDetail,
    ProductionLineDetail,
    useMasterScheduleSlotsContext
} from "../../contexts/MasterScheduleSlotsContext";
import {
    AssemblyInfo,
    BODY_APPLICATION_CATEGORY_ID_STR,
    CAB_STYLE_CATEGORY_ID_STR,
    FRAME_CATEGORY_ID_STR,
    FUEL_TYPE_CATEGORY_ID_STR,
    MotorFamily,
    ProductionLine,
    Quote,
    ScheduledTruck,
    Truck,
    WHEELBASE_CATEGORY_ID_STR
} from "../../api/models";
import useQuoteSelectionInfo from "../../swr/useQuoteSelectionInfo";
import {CustomerAddressBlock} from "../../components/customer_entry";
import {MotorFamilyLineView} from "./MSSlotCalendar";
import {useIntl} from "react-intl";
import {ConfiguratorContext} from "../../context";
import {TruckReportDto} from "../reports/truckReport";
import Paragraph from "antd/lib/typography/Paragraph";

export const DEFAULT_RTS_OFFSET = 5;
export const DEFAULT_RTS_SHIP_OFFSET = 1;
const MSTruckScheduleModal = (props: {
    current?: Dayjs | undefined,
    slotNdx?: number | undefined,
    serialNumber: string | undefined,
    isReadOnly?: boolean | undefined,
    productionLine?: ProductionLineDetail | undefined,
    onChange?: (t: Truck) => void,
    onCancel?: () => void,
}) => {

    const intl = useIntl();
    const configurator = useContext(ConfiguratorContext);

    const truckAsync = useTruck({serialNumber: props.serialNumber});
    const truck = truckAsync.data;

    const quoteAsync = useQuote({quoteRevisionId: truck?.quoteRevisionId});

    const selectionInfoAsync = useQuoteSelectionInfo({quoteRevisionId: truck?.quoteRevisionId});

    const fuelType = selectionInfoAsync.data?.filter(a => a.category?.categoryId === FUEL_TYPE_CATEGORY_ID_STR  )
        .map(a => a.bomDescription ).find(v=>v)

    const msContext = useMasterScheduleSlotsContext();
    const { getProductionLineDetail} = msContext;

    const [selectedDate, setSelectedDate] = useState<Dayjs>();
    const [selectedProductionLine, setSelectedProductionLine] = useState<ProductionLine>();
    const [selectedProductionSlot, setSelectedProductionSlot] = useState<number>();
    const [scheduledShipDate, setScheduledShipDate] = useState<Date | undefined>();
    const [estReadyToShip, setEstReadyToShip] = useState<Date | undefined>();
    const selectedProductionLineDetail = useMemo(() =>
        msContext.getProductionLineDetail?.(selectedDate, selectedProductionLine), [ selectedDate, selectedProductionLine ] );

    //bails out if no serial number
    if ( !props.serialNumber  ) {
        return <></>
    }

    const handleOk = async () => {

        const truckId = truck?.id;
        if ( !truckId ) return;

        const dto : SaveTruckDto = {
            productionLine: selectedProductionLine || null,
            productionSlot: selectedProductionSlot || null,
            productionDate: selectedDate?.toDate() || null,
            scheduledShipDate: scheduledShipDate || null,
            estReadyToShip: estReadyToShip || null,
        }
        const saved = await saveTruck( truckId, dto );
        if ( saved ) {
            truckAsync.mutate();
            props.onChange?.( saved );
        }
    }

    interface SaveTruckDto {
        productionLine?: ProductionLine | undefined | null
        productionSlot?: number | undefined | null
        productionDate?: Date | undefined | null
        scheduledShipDate?: Date | undefined | null
        estReadyToShip?: Date | undefined | null
    }
    const saveTruck = async (truckId: number, dto:SaveTruckDto ) => {
        try {
            const resp = await configurator.api.updateTruck( truckId, dto );
            return resp.data;
        }
        catch(e:any) {
            const id = e.response?.data?.message || e.message ;
            const errorMsg = intl.formatMessage({ id });
            notification.error( { message: "Failed to update truck. " + errorMsg });
        }

        return;

    }

    const handleOpen = () => {
        setSelectedDate(props.current);
        setSelectedProductionLine(props.productionLine?.line);
        setSelectedProductionSlot(props.slotNdx);
        setScheduledShipDate(truck?.scheduledShipDate);
        setEstReadyToShip(truck?.estReadyToShip);
    };
    const handleChangeProductionLine = (line:ProductionLine | undefined) => {
        updateProductionLine(selectedDate, line)
    };

    const updateProductionLine = (dt: Dayjs | undefined, line:ProductionLine | undefined) => {
        setSelectedProductionLine(line);

        if ( dt && line ) {

            const productionLineDetail = msContext.getProductionLineDetail?.(dt, line);
            const motorFamilyDetail = getMotorFamilyLineDetail(fuelType, productionLineDetail);
            const openSlot = (motorFamilyDetail?.serials.length || 0) + 1;
            setSelectedProductionSlot(openSlot);
        }
        else {
            setSelectedProductionSlot(undefined);
        }
    };

    const handleChangeProductionSlot = (slotNdx) => {
        setSelectedProductionSlot(slotNdx);
    };

    const handleSelectDate = (day:Dayjs | undefined) => {

        if (day && isWeekend(day)) {
            notification.error( { message: `Production cannot be scheduled on the weekend.` });
            return;
        }

        updateDate(day);
    };

    const updateDate = (next:Dayjs | undefined) => {
        setSelectedDate(next);
        setEstReadyToShip( next?.add( DEFAULT_RTS_OFFSET, 'day').toDate() );
        setScheduledShipDate( next?.add( DEFAULT_RTS_OFFSET + DEFAULT_RTS_SHIP_OFFSET, 'day').toDate() );
    }

    const findOpenLine = (next:Dayjs) => {
        return  Object.values(ProductionLine).find( l => !isFull(next, l) )
    }

    const isFull = (next:Dayjs, line: ProductionLine) => {
        const nextLineDetail = getProductionLineDetail?.(next, line);
        const motorFamilyDetail = getMotorFamilyLineDetail( fuelType, nextLineDetail )
        const lineOpen = motorFamilyDetail?.lineOpen || 0;

        return lineOpen <= 0;
    }
    const isWeekend = (next:Dayjs) => {
        const weekday = next.weekday();
        return (weekday === 6) || (weekday  === 0); // 6 = Saturday, 0 = Sunday
    }

    const MAX_SEARCH_MONTHS =  3;
    const MAX_SEARCH = 30 * MAX_SEARCH_MONTHS;
    const handleFindOpen = () => {
        const startDate = selectedDate || dayjs();

        let line = selectedProductionLine;
        let maxCnt = 0;
        let next = startDate;
        while ( true ) {
            next = next.add(1, "day");
            if ( maxCnt++ > MAX_SEARCH ) {
                notification.error( { message: `Failed to find an open slot before ${next.format("dddd, MMMM Do YYYY")} months.` });
                return;
            }

            if ( isWeekend(next) ) continue;

            line = findOpenLine(next);
            if ( line ) break;
        }

        updateDate(next);
        updateProductionLine( next, line );
    };

    return <div className={styles["master-schedule-slot-modal"]} key={`${props.serialNumber}-cell`} >
        <Modal
            open={!!props.serialNumber}
            key={`${props.serialNumber}-modal`}
            style={{minWidth: "50rem"}}
            title={<div style={{height: "1rem"}}></div>}
            onClose={props.onCancel}
            onCancel={props.onCancel}
            onOk={handleOk}
            okText={"Save"}
            afterOpenChange={(open) => {
                if( open) {
                    handleOpen();
                }
            }}
        >
            <div style={{display: "flex", gap: "1rem" }}>
                <div style={{flex: "1 1 0"}} >
                    <Skeleton loading={truckAsync.isLoading} >
                        <TruckView truck={truckAsync.data} quote={quoteAsync.data} selections={selectionInfoAsync.data} />
                    </Skeleton>
                </div>
                <div style={{flex: "1 1 0"}} >
                    <div key={`${props.serialNumber}-modal-production-line-view`}
                        style={{
                        paddingBottom: "1rem",
                        borderTop: "2px solid rgba(5, 5, 5, 0.06)",
                        borderBottom: "2px solid rgba(5, 5, 5, 0.06)",
                        textAlign: "right",
                        fontSize: "16px",
                        height: "100%",
                    }} >
                        {selectedDate?.format("dddd, MMMM Do YYYY")}

                        <div style={{marginBottom: "1rem"}} >
                            {(!!selectedProductionLineDetail?.iceTaktRate && selectedDate) &&
                              <MotorFamilyLineView
                                current={selectedDate}
                                motorFamily={MotorFamily.ICE}
                                productionLine={selectedProductionLineDetail}
                                isReadOnly={props.isReadOnly}
                                filteredSerialNumbers={props.serialNumber ? [props.serialNumber] : undefined}
                              /> }

                            {(!!selectedProductionLineDetail?.bevTaktRate && selectedDate) &&
                              <MotorFamilyLineView
                                current={selectedDate}
                                motorFamily={MotorFamily.BEV}
                                productionLine={selectedProductionLineDetail}
                                isReadOnly={props.isReadOnly}
                                filteredSerialNumbers={props.serialNumber ? [props.serialNumber] : undefined}
                              /> }
                        </div>

                        <Divider />

                        <div style={{marginBottom: "1rem"}} >

                            <Descriptions
                                style={{
                                    marginBottom: "1rem",
                                }}
                                column={1}
                                className={descriptionStyles['details-description']}
                                items={[
                                    {
                                        label: "Production Line",
                                        children:

                                            <Select
                                                style={{
                                                    textAlign: "center",
                                                    width: "100%",
                                                }}
                                                placeholder="Select production line"
                                                className={styles["line-selector"]}
                                                onChange={handleChangeProductionLine}
                                                value={selectedProductionLine}
                                                allowClear
                                                options={Object.values(ProductionLine).map((l) => ({
                                                        key: l,
                                                        value: l,
                                                        label: Utils.snakeCaseToFirstLetterCapitalized(l)
                                                    }
                                                ))}
                                            />
                                    },
                                    {
                                        label: "Production Slot",
                                        children:
                                            <div style={{display: "flex", gap: "1rem" }}>
                                                <InputNumber
                                                    onChange={handleChangeProductionSlot}
                                                    value={selectedProductionSlot}
                                                />
                                            </div>
                                    },
                                    {
                                        label: "Production Date",
                                        children:
                                            <div style={{display: "flex", justifyContent: "space-between" }}>
                                                <SelectDate selectedDate={selectedDate?.toDate()} onChange={(d) => {
                                                    handleSelectDate(d ? dayjs(d) : undefined);
                                                } } />
                                                <Button onClick={handleFindOpen}>Find Open</Button>
                                            </div>
                                    },
                                    {
                                        label: "Projected Ready To Ship Date",
                                        children: <div style={{textAlign:"left"}} >
                                            <SelectDate selectedDate={estReadyToShip}  onChange={(d) => {
                                                setEstReadyToShip(d);
                                                setScheduledShipDate( d ? dayjs(d).add( DEFAULT_RTS_SHIP_OFFSET, 'day').toDate() : undefined ) ;
                                            } } />
                                        </div>
                                    },
                                    {
                                        label: "Projected Ship Date",
                                        children: <div style={{textAlign:"left"}} >
                                            <SelectDate selectedDate={scheduledShipDate} onChange={(d) => setScheduledShipDate(d)} />
                                        </div>
                                    },
                                    ]}
                            />
                        </div>

                    </div>
                </div>

            </div>
        </Modal>

    </div>
}

export const SlotView = (props:{
    current?: Dayjs | undefined,
    slotNdx?: number | undefined,
    truck: ScheduledTruck | TruckReportDto | undefined,
    isReadOnly?: boolean | undefined ,
    lineDetail?: FamilyLineDetail | undefined,
    showProductionSlot?: boolean | undefined,
}) => {

    const {filteredTrucks, masterScheduleFlags} = useMasterScheduleSlotsContext();
    const enableSerials = filteredTrucks?.map( t => t.serialNumber );

    const { current, truck, slotNdx } = props;
    const taktRate = props.lineDetail?.taktRate;

    const key = (current || dayjs()).format("YYYYMMDD");

    const isOpen = !props.truck
    const isGood = ( taktRate === undefined || slotNdx === undefined ) || slotNdx < taktRate
    const disabled = props.isReadOnly ? true
        : isOpen ? false
        : !!enableSerials?.length && !!props.truck && !enableSerials.includes(props.truck.serialNumber)
    const color = disabled ? 'rgba(0, 0, 0, 0.25)' :  'rgba(0, 0, 0, 1)'
    const infoBackground =  disabled ? 'rgba(128, 255, 128, 0.25)' :  'rgba(0, 255, 0, 0.25)'
    const errBackground =  disabled ? 'rgba(255, 196, 196, 0.25)' :  'rgba(255, 0, 0, 0.25)';

    const background = !isOpen && isGood ? infoBackground : errBackground;

    const productionDate = current;

    const flags = masterScheduleFlags?.find(f => f.serialNumber == props.truck?.serialNumber);

    const incomplete = !!flags?.incomplete

    const missingSalesOrder = !!flags?.missingSalesOrder

    const pilotInspectionDate = !!flags?.pilotInspectionDate ? dayjs(flags.pilotInspectionDate) : undefined;
    const pilotInspectionFlagColor = pilotInspectionDate?.isBefore(productionDate) ? "rgba(255, 0, 0, 1)" : "rgba(0, 0, 255, 1)"
    const pilotInspectionFlagTitle = pilotInspectionDate?.isBefore(productionDate) ? "The pilot inspection is before the production date." : "There is a pilot inspection scheduled.";

    const customerRequestedShipDate = flags?.requestedShipDate && !dayjs(flags.requestedShipDate).isSame(productionDate) ? dayjs(flags.requestedShipDate) : undefined;
    const customerRequestedFlagColor = pilotInspectionDate?.isBefore(productionDate) ? "rgba(255, 0, 0, 1)" : "rgba(0, 0, 255, 1)"
    const customerRequestedFlagTitle = pilotInspectionDate?.isBefore(productionDate) ? "The production date is after the customer requested date." : "There is a customer requested ship date.";

    const leadTimeWarning = !!flags?.estProductionDate ? dayjs(flags.estProductionDate).isBefore(productionDate) : false;

    var customerName = '';
    if(truck != null) {
        if('customerName' in truck) {
            if(truck.customerName != null) customerName = truck.customerName;
        }
        if('customer' in truck) {
            if(truck.customer != null) customerName = truck.customer;
        }
    }
    const truckName = [truck?.serialNumber , customerName].filter(v=>v).join(" - ");

    return <Tooltip title={truckName}>
        <div
            key={`${key}-${slotNdx}-cell`}
            style={{
                display: "flex",
                gap: "0.2rem",
                justifyContent: "center",
                minWidth: "10rem",
                overflow: "hidden",
                width:'100%',
                border: "2px solid rgba(0, 0, 0, 0.2)",
                borderRadius: "8px",
                padding: "0.1rem",
                background,
                color
            }} >
                <div style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{(props.showProductionSlot && slotNdx != undefined) && <>{slotNdx + 1}: </>} {truckName || "Open" }</div>
                {leadTimeWarning && <ClockCircleOutlined title={"Production Date is within lead time for this vehicle."} style={{color:"orange"}} />}
                {incomplete && <WarningOutlined title={"There is incomplete work on this vehicle."} style={{color:"rgba(255, 0, 0,1)"}} />}
                {pilotInspectionDate && <CalendarOutlined title={pilotInspectionFlagTitle} style={{color: pilotInspectionFlagColor }} />}
                {customerRequestedShipDate && <CalendarOutlined title={customerRequestedFlagTitle} style={{color: customerRequestedFlagColor }} />}
                {missingSalesOrder && <DollarOutlined title={"The sales order is missing."} style={{color:"rgba(255, 0, 0,1)"}} />}
        </div>
    </Tooltip>

}

const TruckView = (props: {
    truck: Truck | undefined,
    quote: Quote | undefined,
    selections: AssemblyInfo[] | undefined,
}) => {

    const { truck, quote, selections } = props;

    const customerItems: DescriptionsItemType[] = [
        {
            label: "Customer",
            children: <div style={{
                color: "black",
                display: "inline-block"
            }}>
                {(quote?.endCustomer)
                    ? <CustomerAddressBlock customer={quote.endCustomer}/>
                    : "None" }
            </div>
        },
        ];

    if ( quote?.owner?.dealerName ) {
        customerItems.splice( 0, 0, {
            label: "Dealer",
            children: quote.owner.dealerName,

        } )
    }


    const cabStyle = selections?.filter(a => a.category?.categoryId === CAB_STYLE_CATEGORY_ID_STR  )
        .map(a => a.bomDescription ).find(v=>v)
    const fuelType = selections?.filter(a => a.category?.categoryId === FUEL_TYPE_CATEGORY_ID_STR  )
        .map(a => a.bomDescription ).find(v=>v)
    const frame = selections?.filter(a => a.category?.categoryId === FRAME_CATEGORY_ID_STR  )
        .map(a => a.bomDescription ).find(v=>v)
    const bodyApplication = selections?.filter(a => a.category?.categoryId === BODY_APPLICATION_CATEGORY_ID_STR  )
        .map(a => a.bomDescription ).find(v=>v)
    const wheelbase = selections?.filter(a => a.category?.categoryId === WHEELBASE_CATEGORY_ID_STR  )
        .map(a => a.bomDescription ).find(v=>v)

    const requestedShipDate = quote?.truckCustomerShippingDateList?.find(t => t.serialNumber == truck?.truckSerialNumberStr )?.truckCustomerShippingDate;

    const pilotInspectionDate = truck?.pilotInspectionDate;

    const truckItems: DescriptionsItemType[] = [
        {
            label: "VIN",
            children:
                <div style={{display: "flex", gap: "0.5rem"}}>
                    {truck?.vin}
                    <Paragraph copyable={{ text: truck?.vin }} style={{margin: 0}} />
                </div>
        },
        {
            label: "Fuel Type",
            children:fuelType || "None"
        },
        {
            label: "Cab Style",
            children: cabStyle || "None"
        },
        {
            label: "Frame",
            children: frame || "None"
        },
        {
            label: "Body",
            children: bodyApplication || "None",
        },
        {
            label: "Wheel Base",
            children: wheelbase || "None",
        },
    ];

    if ( quote?.orderApprovalDate ) {
        truckItems.push( {
            label: "Lead Time",
                children: `${quote?.leadTime} days (est ${dayjs(quote?.orderApprovalDate).subtract(quote?.leadTime || 0).format("MM/DD/YYYY")})`
        });
    }

    if ( requestedShipDate ) {
        truckItems.push( {
            label: "Requested Ship Date",
            children: dayjs(requestedShipDate).format("MM/DD/YYYY")
    });
    }

    if ( pilotInspectionDate ) {
        truckItems.push( {
            label: "Pilot Inspection Date",
            children: dayjs(pilotInspectionDate).format("MM/DD/YYYY")
        });
    }


    return <QuoteContextProvider value={{quote}} >
        <div style={{display: "flex", flexDirection: "column"}} >
            <div style={{
                display: "flex",
                gap: "1rem"
            }} >
                <QuoteTruckImage />

                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    color: "rgba(0, 0, 0, 0.88)",
                    fontWeight: "600",
                    fontSize: "20px",
                    lineHeight: "1.4",
                    textAlign: "left",
                }} >
                    <div style={{display: "flex", gap: "0.5rem"}}>
                        Serial: {truck?.truckSerialNumberStr}
                        <Paragraph copyable={{ text: truck?.truckSerialNumberStr }} style={{margin: 0}} />
                    </div>
                    <div style={{display: "flex", gap: "0.5rem"}}>
                        Part No: <Link to={"/configurator/" + encodeURI(quote?.quoteId || "")} target="_blank">
                            <span data-testid="partNumberString" >{quote?.partNumberString || "NA"}</span>
                        </Link>
                        <Paragraph copyable={{ text: quote?.partNumberString }} style={{margin: 0}} />
                    </div>
                </div>

            </div>


                <div style={{marginBottom: "1rem"}} >
                    <Descriptions
                        layout={"vertical"}
                        className={descriptionStyles['details-description']}
                        size="small"
                        items={customerItems}
                    />
                </div>
                <div style={{marginBottom: "1rem"}} >
                    <Descriptions
                        layout={"vertical"}
                        column={1}
                        className={descriptionStyles['details-description']}
                        size="small"
                        items={truckItems}
                    />
                </div>
                <Card size={"small"}>
                    <TruckCommentList title={"Comments"} serialNumber={truck?.truckSerialNumberStr} />
                </Card>
            </div>
    </QuoteContextProvider>;

}

const SelectDate = (props:{
    selectedDate:Date | undefined
    onChange?: (dt:Date) => void
    isReadOnly?: boolean
}) => {

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const btnStyle = {borderBottom: "1px solid black"};
    const [shipDate, setShipDate] = useState<Date>();
    const inputRef = useRef<InputRef>(null);

    const handleOpen = () => {
        setShipDate( props.selectedDate );
        setIsOpen(true);
    }
    const handleBlur = () => {
        setIsOpen(false);
    }

    const btnTxt = props.selectedDate ? dayjs(props.selectedDate).format("M/DD/YYYY") : "None";

    return <Popover
        open={isOpen}
        afterOpenChange={(open) => {
            if( open ) {
                inputRef.current?.focus();
            }
        }}
        content={<>
            { //@ts-ignore
                <DatePicker placeholder="Shipping Date" ref={inputRef} allowClear
                            value={shipDate ? dayjs(shipDate) : undefined}
                            onChange={(dt) => props.onChange?.(dt?.toDate() ) }
                            onBlur={handleBlur}
                />}
        </>}
    >
        {!props.isReadOnly
            ? <Button className="ghostBmButton" type="text" onClick={handleOpen} style={{"padding": 0}} >
                <span style={btnStyle}>{btnTxt}</span>
            </Button>
            : btnTxt}
    </Popover>
}


export default MSTruckScheduleModal;
